
import React from "react";

import { useNavigate, useParams } from "react-router-dom";

import { useGetCenterBillingHistoryQuery } from "../../slices/billingDashboardApiSlice";

const CoachingCenterBillingHistoryScreen = () => {
  const navigate = useNavigate();

  const { centerId } = useParams();

  const { data, isLoading, error } = useGetCenterBillingHistoryQuery(centerId);

  const billing = data?.data;

  // =====================================================
  // Helpers
  // =====================================================

  const formatCurrency = (amount = 0) => {
    return `₹${Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatStatus = (status) => {
    if (!status) return "-";

    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatPaymentMethod = (method) => {
    if (!method) return "-";

    return method
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: "bg-green-100 text-green-700",
      pending: "bg-yellow-100 text-yellow-700",
      cancelled: "bg-red-100 text-red-700",
      overdue: "bg-orange-100 text-orange-700",

      trial: "bg-blue-100 text-blue-700",
      active: "bg-green-100 text-green-700",
      expired: "bg-gray-200 text-gray-700",
    };

    return (
      <span
        className={`
          inline-flex
          px-3
          py-1
          rounded-full
          text-xs
          font-semibold
          ${styles[status] || "bg-gray-100 text-gray-700"}
        `}
      >
        {formatStatus(status)}
      </span>
    );
  };

  // =====================================================
  // Loading
  // =====================================================

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div
          className="
            animate-spin
            h-10
            w-10
            border-4
            border-blue-600
            border-t-transparent
            rounded-full
          "
        />
      </div>
    );
  }

  // =====================================================
  // Error
  // =====================================================

  if (error) {
    return (
      <div
        className="
          bg-red-50
          border
          border-red-200
          text-red-700
          rounded-lg
          p-5
        "
      >
        {error?.data?.message ||
          error?.error ||
          "Unable to load billing history"}
      </div>
    );
  }

  // =====================================================
  // No Data
  // =====================================================

  if (!billing) {
    return (
      <div
        className="
          bg-white
          rounded-lg
          shadow
          p-8
          text-center
          text-gray-500
        "
      >
        Billing history not found.
      </div>
    );
  }

  const summary = billing.summary || {};

  const invoices = billing.invoices || [];

  // =====================================================
  // Render
  // =====================================================

  return (
    <div className="space-y-6">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-800">
            Coaching Center Billing History
          </h2>

          <p className="text-gray-500 mt-1">
            Subscription, invoices and payment history
          </p>
        </div>

        <button
          onClick={() => navigate(-1)}
          className="
            self-start
            px-4
            py-2
            bg-gray-600
            hover:bg-gray-700
            text-white
            rounded-lg
          "
        >
          Back
        </button>
      </div>

      {/* =====================================================
          CENTER INFORMATION
      ===================================================== */}

      <div className="bg-white shadow rounded-lg">
        <div className="px-5 py-3 border-b font-semibold text-gray-800">
          Coaching Center Information
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-6">
          <InfoBlock label="Name" value={billing.center?.name} />

          <InfoBlock label="Email" value={billing.center?.email} />

          <InfoBlock label="Phone" value={billing.center?.phone} />
        </div>
      </div>

      {/* =====================================================
          SUBSCRIPTION
      ===================================================== */}

      {billing.subscription && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-5 py-3 border-b font-semibold text-gray-800">
            Current Subscription
          </div>

          <div className="p-5 grid grid-cols-1 md:grid-cols-4 gap-6">
            <InfoBlock
              label="Plan"
              value={
                billing.subscription.planName ||
                billing.subscription.plan ||
                billing.subscription.name
              }
            />

            <div>
              <p className="text-sm text-gray-500">Status</p>

              <div className="mt-2">
                {getStatusBadge(billing.subscription.status)}
              </div>
            </div>

            <InfoBlock
              label="Start Date"
              value={formatDate(
                billing.subscription.startDate ||
                  billing.subscription.currentPeriodStart,
              )}
            />

            <InfoBlock
              label="Next Billing"
              value={formatDate(
                billing.subscription.nextBillingDate ||
                  billing.subscription.currentPeriodEnd,
              )}
            />
          </div>
        </div>
      )}

      {/* =====================================================
          BILLING SUMMARY
      ===================================================== */}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-5">
        <SummaryCard
          title="Total Invoices"
          value={summary.totalInvoices || 0}
          color="bg-blue-600"
        />

        <SummaryCard
          title="Total Billed"
          value={formatCurrency(summary.totalBilled)}
          color="bg-indigo-600"
        />

        <SummaryCard
          title="Total Paid"
          value={formatCurrency(summary.totalPaid)}
          color="bg-green-600"
        />

        <SummaryCard
          title="Outstanding"
          value={formatCurrency(summary.outstanding)}
          color={
            Number(summary.outstanding || 0) > 0 ? "bg-red-600" : "bg-green-600"
          }
        />

        <SummaryCard
          title="GST"
          value={formatCurrency(summary.totalTax)}
          color="bg-purple-600"
        />
      </div>

      {/* =====================================================
          BILLING SUMMARY DETAILS
      ===================================================== */}

      <div className="bg-white shadow rounded-lg">
        <div className="px-5 py-3 border-b font-semibold text-gray-800">
          Billing Summary
        </div>

        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <SummaryDetail
            label="Total Billed"
            value={formatCurrency(summary.totalBilled)}
          />

          <SummaryDetail
            label="Taxable Amount"
            value={formatCurrency(summary.totalTaxable)}
          />

          <SummaryDetail
            label="GST"
            value={formatCurrency(summary.totalTax)}
            valueClass="text-purple-600"
          />

          <SummaryDetail
            label="Total Discount"
            value={formatCurrency(summary.totalDiscount)}
            valueClass="text-red-600"
          />

          <SummaryDetail
            label="Total Paid"
            value={formatCurrency(summary.totalPaid)}
            valueClass="text-green-600"
          />

          <SummaryDetail
            label="Outstanding"
            value={formatCurrency(summary.outstanding)}
            valueClass={
              Number(summary.outstanding || 0) > 0
                ? "text-red-600"
                : "text-green-600"
            }
          />
        </div>
      </div>

      {/* =====================================================
          INVOICE HISTORY
      ===================================================== */}

      <div className="bg-white shadow rounded-lg">
        <div className="px-5 py-3 border-b">
          <div className="flex flex-col md:flex-row justify-between gap-2">
            <div>
              <h3 className="font-semibold text-lg text-gray-800">
                Invoice History
              </h3>

              <p className="text-sm text-gray-500 mt-1">
                {invoices.length} invoice
                {invoices.length === 1 ? "" : "s"}
              </p>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left">Invoice</th>

                <th className="px-4 py-3 text-left">Invoice Date</th>

                <th className="px-4 py-3 text-left">Billing Period</th>

                <th className="px-4 py-3 text-right">Taxable</th>

                <th className="px-4 py-3 text-right">Discount</th>

                <th className="px-4 py-3 text-right">GST</th>

                <th className="px-4 py-3 text-right">Total</th>

                <th className="px-4 py-3 text-right">Paid</th>

                <th className="px-4 py-3 text-right">Balance</th>

                <th className="px-4 py-3 text-left">Status</th>

                <th className="px-4 py-3 text-left">Action</th>
              </tr>
            </thead>

            <tbody>
              {!invoices.length ? (
                <tr>
                  <td
                    colSpan="11"
                    className="
                      text-center
                      p-10
                      text-gray-500
                    "
                  >
                    No invoices found for this coaching center.
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => {
                  const invoiceBalance = Math.max(
                    Number(invoice.total || 0) -
                      Number(invoice.paidAmount || 0),
                    0,
                  );

                  return (
                    <tr
                      key={invoice._id}
                      className="
                        border-t
                        hover:bg-gray-50
                      "
                    >
                      {/* Invoice */}

                      <td className="px-4 py-4">
                        <button
                          onClick={() =>
                            navigate(`/billing/invoices/${invoice._id}`)
                          }
                          className="
                            font-semibold
                            text-blue-600
                            hover:text-blue-800
                            hover:underline
                          "
                        >
                          {invoice.invoiceNumber}
                        </button>

                        <p className="text-xs text-gray-500 mt-1">
                          {invoice.gstType
                            ? invoice.gstType.toUpperCase()
                            : "GST"}
                        </p>
                      </td>

                      {/* Invoice Date */}

                      <td className="px-4 py-4 whitespace-nowrap">
                        {formatDate(invoice.invoiceDate)}
                      </td>

                      {/* Billing Period */}

                      <td className="px-4 py-4 whitespace-nowrap">
                        <div>{formatDate(invoice.billingPeriodStart)}</div>

                        <div className="text-gray-500">
                          to {formatDate(invoice.billingPeriodEnd)}
                        </div>
                      </td>

                      {/* Taxable */}

                      <td className="px-4 py-4 text-right whitespace-nowrap">
                        {formatCurrency(invoice.taxableAmount)}
                      </td>

                      {/* Discount */}

                      <td className="px-4 py-4 text-right whitespace-nowrap">
                        {Number(invoice.discountAmount || 0) > 0 ? (
                          <span className="text-red-600">
                            -{formatCurrency(invoice.discountAmount)}
                          </span>
                        ) : (
                          formatCurrency(0)
                        )}
                      </td>

                      {/* GST */}

                      <td className="px-4 py-4 text-right whitespace-nowrap">
                        <div>{formatCurrency(invoice.totalTaxAmount)}</div>

                        <div className="text-xs text-gray-500 mt-1">
                          {invoice.gstRate || 0}%
                        </div>
                      </td>

                      {/* Total */}

                      <td className="px-4 py-4 text-right whitespace-nowrap">
                        <strong className="text-gray-900">
                          {formatCurrency(invoice.total)}
                        </strong>
                      </td>

                      {/* Paid */}

                      <td className="px-4 py-4 text-right whitespace-nowrap">
                        <span className="text-green-600">
                          {formatCurrency(invoice.paidAmount)}
                        </span>
                      </td>

                      {/* Balance */}

                      <td className="px-4 py-4 text-right whitespace-nowrap">
                        <span
                          className={
                            invoiceBalance > 0
                              ? "text-red-600 font-semibold"
                              : "text-green-600 font-semibold"
                          }
                        >
                          {formatCurrency(invoiceBalance)}
                        </span>
                      </td>

                      {/* Status */}

                      <td className="px-4 py-4">
                        {getStatusBadge(invoice.status)}
                      </td>

                      {/* Action */}

                      <td className="px-4 py-4">
                        <button
                          onClick={() =>
                            navigate(`/billing/invoices/${invoice._id}`)
                          }
                          className="
                            px-3
                            py-1.5
                            border
                            border-blue-600
                            text-blue-600
                            rounded
                            hover:bg-blue-600
                            hover:text-white
                            whitespace-nowrap
                          "
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* =====================================================
          BILLING NOTE
      ===================================================== */}

      <div
        className="
          bg-blue-50
          border
          border-blue-100
          rounded-lg
          p-4
          text-sm
          text-blue-700
        "
      >
        <p className="font-semibold">Center-level billing</p>

        <p className="mt-1">
          This history contains invoices and billing totals associated with{" "}
          <span className="font-semibold">
            {billing.center?.name || "this coaching center"}
          </span>
          .
        </p>
      </div>
    </div>
  );
};

// =====================================================
// Info Block
// =====================================================

const InfoBlock = ({ label, value }) => {
  return (
    <div>
      <p className="text-sm text-gray-500">{label}</p>

      <p className="font-semibold text-gray-800 mt-1">{value || "-"}</p>
    </div>
  );
};

// =====================================================
// Summary Card
// =====================================================

const SummaryCard = ({ title, value, color }) => {
  return (
    <div
      className={`
        ${color}
        text-white
        rounded-lg
        shadow
        p-5
      `}
    >
      <p className="text-sm opacity-90">{title}</p>

      <h2 className="text-2xl font-bold mt-2">{value || 0}</h2>
    </div>
  );
};

// =====================================================
// Summary Detail
// =====================================================

const SummaryDetail = ({ label, value, valueClass = "text-gray-800" }) => {
  return (
    <div>
      <p className="text-sm text-gray-500">{label}</p>

      <p
        className={`
          text-xl
          font-bold
          mt-1
          ${valueClass}
        `}
      >
        {value}
      </p>
    </div>
  );
};

export default CoachingCenterBillingHistoryScreen;
